import type {SubIssuesListItemNode} from '../components/sub-issues-list-item-query-types'
import type {AddSubIssuePayload, RemoveSubIssuePayload} from './sub-issue-membership-api'
import {buildSubIssueNodeFromServer} from './sub-issue-optimistic-node'
import type {Disposable, Environment, RecordProxy, RecordSourceProxy} from 'relay-runtime'

const SUB_ISSUES_ARGS = {first: 100}

function getOrCreateRecord(store: RecordSourceProxy, id: string, typeName: string): RecordProxy {
  return store.get(id) ?? store.create(id, typeName)
}

function getSubIssues(parent: RecordProxy) {
  return parent.getLinkedRecord('subIssues', SUB_ISSUES_ARGS)
}

function writeSummary(store: RecordSourceProxy, parent: RecordProxy, summary: {total: number; completed: number}) {
  const record =
    parent.getLinkedRecord('subIssuesSummary') ??
    store.create(`${parent.getDataID()}:native-membership:subIssuesSummary`, 'SubIssuesSummary')
  record.setValue(summary.total, 'total')
  record.setValue(summary.completed, 'completed')
  record.setValue(summary.total === 0 ? 0 : Math.floor((summary.completed / summary.total) * 100), 'percentCompleted')
  parent.setLinkedRecord(record, 'subIssuesSummary')

  parent.getLinkedRecord('subIssuesConnection')?.setValue(summary.total, 'totalCount')
  getSubIssues(parent)?.setValue(summary.total, 'totalCount')
}

function writeSubIssue(store: RecordSourceProxy, payload: AddSubIssuePayload['subIssue']): RecordProxy {
  const node: SubIssuesListItemNode = buildSubIssueNodeFromServer(payload)
  const issue = getOrCreateRecord(store, node.id, 'Issue')
  issue.setValue(node.id, 'id')
  issue.setValue(node.closed, 'closed')
  issue.setValue(node.state, 'state')
  issue.setValue(node.stateReason, 'stateReason')
  issue.setValue(node.url, 'url')
  issue.setValue(node.databaseId, 'databaseId')
  issue.setValue(node.number, 'number')
  issue.setValue(node.title, 'title')
  // This is server-rendered markup from the persisted mutation response; escaping would double-escape it.
  // eslint-disable-next-line @github-ui/github-monorepo/no-unsafe-titlehtml
  issue.setValue(node.titleHTML, 'titleHTML')

  const repository = getOrCreateRecord(store, payload.repository.id, 'Repository')
  repository.setValue(payload.repository.id, 'id')
  repository.setValue(node.repository.name, 'name')
  const owner = getOrCreateRecord(store, payload.repository.owner.id, payload.repository.owner.__typename)
  owner.setValue(payload.repository.owner.id, 'id')
  owner.setValue(node.repository.owner.login, 'login')
  repository.setLinkedRecord(owner, 'owner')
  issue.setLinkedRecord(repository, 'repository')

  const dependencies =
    issue.getLinkedRecord('issueDependenciesSummary') ??
    store.create(`${node.id}:native-membership:issueDependenciesSummary`, 'IssueDependenciesSummary')
  dependencies.setValue(node.issueDependenciesSummary.blockedBy, 'blockedBy')
  issue.setLinkedRecord(dependencies, 'issueDependenciesSummary')

  const assignees =
    issue.getLinkedRecord('assignees', {first: 10}) ??
    store.create(`${node.id}:native-membership:assignees`, 'UserConnection')
  assignees.setValue(node.assignees.totalCount, 'totalCount')
  assignees.setLinkedRecords(
    (node.assignees.edges ?? []).flatMap((edge, index) => {
      if (!edge?.node) return []
      const edgeRecord = getOrCreateRecord(store, `${assignees.getDataID()}:edge:${index}`, 'UserEdge')
      const assignee = getOrCreateRecord(store, edge.node.id, 'User')
      assignee.setValue(edge.node.id, 'id')
      assignee.setValue(edge.node.login, 'login')
      assignee.setValue(edge.node.avatarUrl, 'avatarUrl')
      edgeRecord.setLinkedRecord(assignee, 'node')
      return [edgeRecord]
    }),
    'edges',
  )
  issue.setLinkedRecord(assignees, 'assignees', {first: 10})

  if (node.issueType) {
    const issueType = getOrCreateRecord(store, node.issueType.id, 'IssueType')
    issueType.setValue(node.issueType.name, 'name')
    issueType.setValue(node.issueType.color, 'color')
    issue.setLinkedRecord(issueType, 'issueType')
  } else {
    issue.setValue(null, 'issueType')
  }

  const summary =
    issue.getLinkedRecord('subIssuesSummary') ??
    store.create(`${node.id}:native-membership:subIssuesSummary`, 'SubIssuesSummary')
  summary.setValue(node.subIssuesSummary.total, 'total')
  summary.setValue(node.subIssuesSummary.completed, 'completed')
  issue.setLinkedRecord(summary, 'subIssuesSummary')

  const pullRequests =
    issue.getLinkedRecord('closedByPullRequestsReferences', {first: 0, includeClosedPrs: true}) ??
    store.create(`${node.id}:native-membership:closedByPullRequestsReferences`, 'PullRequestConnection')
  pullRequests.setValue(node.closedByPullRequestsReferences?.totalCount ?? 0, 'totalCount')
  issue.setLinkedRecord(pullRequests, 'closedByPullRequestsReferences', {first: 0, includeClosedPrs: true})

  return issue
}

function writeParentLink(store: RecordSourceProxy, subIssueId: string, parentIssueId: string | null) {
  const subIssue = store.get(subIssueId)
  if (!subIssue) return
  if (parentIssueId === null) {
    subIssue.setValue(null, 'parent')
    return
  }

  const parent = store.get(parentIssueId)
  if (parent) subIssue.setLinkedRecord(parent, 'parent')
}

export function applyRelayParentLink(
  environment: Environment,
  subIssueId: string,
  parentIssueId: string | null,
): Disposable {
  return environment.applyUpdate({
    storeUpdater: store => writeParentLink(store, subIssueId, parentIssueId),
  })
}

export function commitRelayAddSubIssue(environment: Environment, parentIssueId: string, payload: AddSubIssuePayload) {
  environment.commitUpdate(store => {
    const child = writeSubIssue(store, payload.subIssue)
    const parent = store.get(parentIssueId)
    if (!parent) return

    const subIssues = getSubIssues(parent)
    const nodes = subIssues?.getLinkedRecords('nodes') ?? []
    if (subIssues && !nodes.some(node => node?.getDataID() === child.getDataID())) {
      subIssues.setLinkedRecords([...nodes, child], 'nodes')
    }
    writeSummary(store, parent, payload.issue.subIssuesSummary)
    child.setLinkedRecord(parent, 'parent')
  })
}

export function commitRelayRemoveSubIssue(
  environment: Environment,
  parentIssueId: string,
  payload: RemoveSubIssuePayload,
) {
  environment.commitUpdate(store => {
    const parent = store.get(parentIssueId)
    if (parent) {
      const subIssues = getSubIssues(parent)
      const nodes = subIssues?.getLinkedRecords('nodes') ?? []
      subIssues?.setLinkedRecords(
        nodes.filter(node => node?.getDataID() !== payload.subIssue.id),
        'nodes',
      )
      writeSummary(store, parent, payload.issue.subIssuesSummary)
    }
    writeParentLink(store, payload.subIssue.id, payload.subIssue.parent?.id ?? null)
  })
}
